/**
 * ============================================
 * AUTH CONTEXT - Contexto de Autenticación
 * ============================================
 *
 * Maneja el estado global de autenticación:
 * - Usuario actual
 * - Tokens JWT
 * - Funciones de login/logout
 *
 * USO:
 * import { useAuth } from '@/contexts/auth-context';
 * const { user, login, logout, isAuthenticated } = useAuth();
 */

/* eslint-disable react-refresh/only-export-components */
// Deshabilitado porque es patrón estándar exportar Provider + hook desde mismo archivo

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api } from "@/lib/api";

// ============================================
// TIPOS
// ============================================

/** Datos del usuario autenticado */
export interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  role: {
    id: number;
    name: string;
  } | null;
  sucursal: {
    id: number;
    nombre: string;
  } | null;
  permissions: string[];
}

/** Respuesta del endpoint /auth/login/ */
interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

/** Respuesta del endpoint /auth/me/ - misma estructura que User */
type MeResponse = User;

/** Estado del contexto de autenticación */
interface AuthContextType {
  /** Usuario autenticado o null */
  user: User | null;
  /** True si hay un usuario autenticado */
  isAuthenticated: boolean;
  /** True mientras se verifica la sesión inicial */
  isLoading: boolean;
  /** Iniciar sesión */
  login: (email: string, password: string) => Promise<void>;
  /** Cerrar sesión */
  logout: () => Promise<void>;
  /** Verificar si el usuario tiene un permiso específico */
  hasPermission: (module: string, action: string) => boolean;
}

// ============================================
// CONTEXTO
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// STORAGE KEYS
// ============================================

const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  USER: "user",
} as const;

// ============================================
// PROVIDER
// ============================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Guardar tokens y usuario en localStorage
   */
  const saveAuthData = useCallback((accessToken: string, refreshToken: string, userData: User) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    setUser(userData);
  }, []);

  /**
   * Limpiar datos de autenticación
   */
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setUser(null);
  }, []);

  /**
   * Verificar sesión existente al cargar la app
   */
  const checkAuth = useCallback(async () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      // Verificar que el token es válido llamando a /me
      const response = await api.get<MeResponse>("/auth/me/");
      setUser(response.data);
    } catch {
      // Token inválido o expirado - el interceptor intentará refresh
      // Si falla el refresh, limpiará los tokens automáticamente
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuthData]);

  /**
   * Iniciar sesión
   */
  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<LoginResponse>("/auth/login/", {
        email,
        password,
      });

      const { access, refresh, user: userData } = response.data;
      saveAuthData(access, refresh, userData);
    },
    [saveAuthData]
  );

  /**
   * Cerrar sesión
   */
  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (refreshToken) {
      try {
        // Invalidar el refresh token en el backend
        await api.post("/auth/logout/", { refresh: refreshToken });
      } catch {
        // Ignorar errores - igual limpiamos localmente
      }
    }

    clearAuthData();
  }, [clearAuthData]);

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  const hasPermission = useCallback(
    (module: string, action: string): boolean => {
      if (!user) return false;

      // Superusuarios tienen todos los permisos
      if (user.permissions.includes("*")) return true;

      // Verificar permiso específico
      return user.permissions.includes(`${module}.${action}`);
    },
    [user]
  );

  // Verificar sesión al montar el componente
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================
// HOOK
// ============================================

/**
 * Hook para acceder al contexto de autenticación
 *
 * @example
 * const { user, login, logout, isAuthenticated } = useAuth();
 *
 * // Verificar permiso
 * if (hasPermission('ordenes', 'create')) {
 *   // Mostrar botón de crear orden
 * }
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }

  return context;
}
